import React, { useEffect, useMemo, useState } from 'react';
import { BackHandler, Platform, Pressable, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { theme } from '@/config/theme';
import { hasSeenChecklistActionsTip, markChecklistActionsTipSeen } from '@/services/checklist/checklistActionsTip';

type TipFrame = { x: number; y: number; width: number; height: number; hostWidth: number; hostHeight: number };
type Props = {
  userId: string | null;
  ready: boolean;
  anchorRef: React.RefObject<View | null>;
  hostRef: React.RefObject<View | null>;
};

export const checklistActionsTipLayout = (frame: TipFrame, cardHeight: number) => {
  const margin = theme.spacing.md;
  const cardWidth = Math.min(340, Math.max(0, frame.hostWidth - margin * 2));
  const centerX = frame.x + frame.width / 2;
  const left = Math.max(margin, Math.min(centerX - cardWidth / 2, frame.hostWidth - cardWidth - margin));
  const below = frame.hostHeight - frame.y - frame.height - margin >= cardHeight || frame.y < cardHeight + margin;
  const top = Math.max(margin, Math.min(
    below ? frame.y + frame.height + 10 : frame.y - cardHeight - 10,
    frame.hostHeight - cardHeight - margin
  ));
  return { cardWidth, left, top, below, pointerLeft: Math.max(14, Math.min(centerX - left - 6, cardWidth - 26)) };
};

/** Reutiliza o destaque ancorado do antigo aviso de QR, sem apresentar um Modal nativo. */
export const ChecklistActionsTip = ({ userId, ready, anchorRef, hostRef }: Props) => {
  const dimensions = useWindowDimensions();
  const [offerTo, setOfferTo] = useState<string | null>(null);
  const [frame, setFrame] = useState<TipFrame | null>(null);
  const [cardHeight, setCardHeight] = useState(190);

  useEffect(() => {
    setOfferTo(userId && !hasSeenChecklistActionsTip(userId) ? userId : null);
    setFrame(null);
  }, [userId]);

  useEffect(() => {
    if (!ready || !userId || offerTo !== userId) return;
    let active = true;
    const timer = setTimeout(() => {
      hostRef.current?.measureInWindow((hostX, hostY, hostWidth, hostHeight) => {
        anchorRef.current?.measureInWindow((x, y, width, height) => {
          if (!active || ![hostX, hostY, hostWidth, hostHeight, x, y, width, height].every(Number.isFinite)
            || Math.min(hostWidth, hostHeight, width, height) <= 0) return;
          setFrame({ x: x - hostX, y: y - hostY, width, height, hostWidth, hostHeight });
        });
      });
    }, 90);
    return () => { active = false; clearTimeout(timer); };
  }, [ready, userId, offerTo, anchorRef, hostRef, dimensions.width, dimensions.height]);

  const visible = ready && Boolean(userId) && offerTo === userId && frame !== null;
  const layout = useMemo(() => frame ? checklistActionsTipLayout(frame, cardHeight) : null, [frame, cardHeight]);
  const dismiss = () => setOfferTo(null);
  useEffect(() => {
    if (!visible || !userId) return;
    markChecklistActionsTipSeen(userId);
    if (Platform.OS === 'web') {
      const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); setOfferTo(null); } };
      document.addEventListener('keydown', escape);
      return () => document.removeEventListener('keydown', escape);
    }
    const back = BackHandler.addEventListener('hardwareBackPress', () => { setOfferTo(null); return true; });
    return () => back.remove();
  }, [visible, userId]);

  if (!visible || !frame || !layout) return null;
  return <View style={styles.portal} accessibilityViewIsModal>
    <Pressable style={styles.backdrop} onPress={dismiss} accessibilityRole="button" accessibilityLabel="Fechar dica de Ações" />
    <View pointerEvents="none" style={[styles.highlight, { left: Math.max(0, frame.x - 5), top: Math.max(0, frame.y - 6), width: frame.width + 10, height: frame.height + 12 }]} />
    <LinearGradient colors={theme.colors.primary.gradient} style={[styles.card, { left: layout.left, top: layout.top, width: layout.cardWidth }]}
      onLayout={event => setCardHeight(event.nativeEvent.layout.height)}>
      <View style={styles.heading}>
        <Text style={styles.pill}>Novidade</Text>
        <TouchableOpacity onPress={dismiss} style={styles.close} accessibilityRole="button" accessibilityLabel="Fechar dica de Ações">
          <Ionicons name="close" size={18} color={theme.colors.primary.contrast} />
        </TouchableOpacity>
      </View>
      <Text style={styles.title}>As ações estão aqui</Text>
      <Text style={styles.description}>As ações do checklist mudaram de lugar. Toque em Ações (•••), no topo, para encontrar as opções disponíveis.</Text>
      <TouchableOpacity onPress={dismiss} style={styles.cta} accessibilityRole="button" accessibilityLabel="Entendi o novo local das ações">
        <Text style={styles.ctaText}>Entendi</Text>
      </TouchableOpacity>
      <View style={[styles.pointer, layout.below ? { top: -6 } : { bottom: -6 }, { left: layout.pointerLeft }]} />
    </LinearGradient>
  </View>;
};

const styles = StyleSheet.create({
  portal: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: theme.zIndex.overlay, elevation: theme.zIndex.overlay },
  backdrop: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(9,17,34,0.34)' },
  highlight: { position: 'absolute', borderRadius: theme.borderRadius.md, borderWidth: 2, borderColor: 'rgba(255,255,255,0.82)' },
  card: { position: 'absolute', padding: theme.spacing.lg, borderRadius: theme.borderRadius.lg, ...theme.shadows.md },
  heading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  pill: { paddingHorizontal: theme.spacing.sm, paddingVertical: 2, borderRadius: theme.borderRadius.pill, backgroundColor: 'rgba(255,255,255,0.2)', color: theme.colors.primary.contrast, fontSize: theme.fontSizes.xs },
  close: { width: 32, height: 32, borderRadius: theme.borderRadius.pill, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  title: { marginTop: theme.spacing.sm, color: theme.colors.primary.contrast, fontSize: theme.fontSizes.lg, fontWeight: '600' },
  description: { marginTop: theme.spacing.sm, color: theme.colors.primary.contrast, fontSize: theme.fontSizes.sm, lineHeight: 20 },
  cta: { marginTop: theme.spacing.md, alignSelf: 'flex-start', borderRadius: theme.borderRadius.pill, paddingVertical: theme.spacing.sm, paddingHorizontal: theme.spacing.md, backgroundColor: 'rgba(255,255,255,0.22)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.34)' },
  ctaText: { color: theme.colors.primary.contrast, fontSize: theme.fontSizes.sm, fontWeight: '700' },
  pointer: { position: 'absolute', width: 12, height: 12, backgroundColor: theme.colors.primary.default, transform: [{ rotate: '45deg' }] },
});
